import React from 'react'

import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router'
import { createQueryClient, ErrorBoundary, useUserService, UserProvider, HttpProvider, PrivateRoute } from '@climate/components'
import StackedLayout from './layouts/Stacked/Layout'

import { Login, Logout, MfaChallenge, MfaSetup, Onboard, Register } from './views'
import AcceptInvitation from './views/AcceptInvitation'

const AppRoutes = () => {
  const { user } = useUserService(window?.config?.iam_service_url, window?.config?.session_cookie, 'iam')

  const defaultRoute = user.entity_status !== 'active' ? '/app/onboard' : '/app/home'

  return (
    <Routes>
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />
      <Route path="/app/login" element={<Login />} />
      {/*
        Both halves of the second step. Public routes, because the person
        reaching them has no session yet: that is the whole point of the step.
        What lets them in is the MFA token the login screen handed over in
        router state, which each screen checks for itself.
      */}
      <Route path="/app/mfa" element={<MfaChallenge />} />
      <Route path="/app/mfa/setup" element={<MfaSetup />} />
      <Route path="/app/logout" element={<Logout />} />
      <Route path="/app/register" element={<Register />} />
      <Route path="/app/accept_invitation" element={<AcceptInvitation />} />
      <Route path="/app/onboard" element={<PrivateRoute component={Onboard} scope="iam" />} />
      <Route path="/app/*" element={<PrivateRoute component={StackedLayout} scope="iam" />} />
    </Routes>
  )
}

function WithRouter () {
  const navigate = useNavigate()
  return (
    <ErrorBoundary history={{ push: (path: string) => navigate(path) }}>
      <AppRoutes />
    </ErrorBoundary>
  )
}

const queryClient = createQueryClient()

export default function App () {
  return (
    <React.StrictMode>
      <Toaster position="bottom-right" toastOptions={{ duration: 5000 }} />
      <QueryClientProvider client={queryClient}>
        <HttpProvider sessionCookieName={window?.config?.session_cookie}>
          <UserProvider>
            <BrowserRouter>
              <WithRouter />
            </BrowserRouter>
          </UserProvider>
        </HttpProvider>
      </QueryClientProvider>
    </React.StrictMode>
  )
}
